import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { policyCoverageName } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { Button } from '@/shared/ui/Button';
import { DetailCard } from '@/shared/ui/DetailLayout';
import { Field } from '@/shared/ui/Field';
import { useToast } from '@/shared/ui/useToast';

/**
 * "Impressao na apolice" da modalidade (P-MIN-2): o nome da cobertura que o
 * frontispicio imprime em MODALIDADE/COBERTURA, que nao e o nome da modalidade (no
 * OnPoint, "EXECUCAO DE CONTRATO CONSTRUCAO(ECC)" e "LICITANTE"; 48, MIN-061). Vazio
 * e "nao informado": o documento sai com o campo vazio.
 */
export function NomeDaCoberturaImpresso({
  productId,
  modalityId,
}: {
  productId: string;
  modalityId: string;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const chave = ['policy-coverage-name', productId, modalityId];
  const consulta = useQuery({
    queryKey: chave,
    queryFn: ({ signal }) => policyCoverageName.get(productId, modalityId, signal),
    retry: false,
  });
  const [nome, setNome] = useState<string | null>(null);
  const valor = nome ?? consulta.data?.policy_coverage_name ?? '';
  const podeEditar = can('produtos.produto.update');

  const salvar = useMutation({
    mutationFn: () => policyCoverageName.save(productId, modalityId, valor.trim()),
    onSuccess: (dados) => {
      toast.success('Nome da cobertura impresso gravado.');
      queryClient.setQueryData(chave, dados);
      setNome(null);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (consulta.isError) return null;

  return (
    <DetailCard
      title="Impressao na apolice"
      description="O nome que o documento da apolice imprime em MODALIDADE/COBERTURA."
      loading={consulta.isPending}
      action={
        podeEditar ? (
          <Button
            size="sm"
            loading={salvar.isPending}
            disabled={nome === null}
            onClick={() => salvar.mutate()}
          >
            Salvar nome impresso
          </Button>
        ) : null
      }
    >
      <Field
        label="Nome da cobertura impresso"
        value={valor}
        maxLength={200}
        disabled={!podeEditar}
        onChange={(event) => setNome(event.target.value)}
      />
    </DetailCard>
  );
}
